/**
 * Agrega negociações para a Análise de Vendas IA (por canal: ganhos, perdas, vendas).
 */

import type { Lead } from '../../../types';

export type CanalNegociacaoStats = {
    total: number;
    ganhos: number;
    perdidos: number;
    emAberto: number;
    /** Soma do valor das negociações ganhas neste canal. */
    valorVendas: number;
};

export type LeadsAnalysisSummary = {
    total: number;
    valorTotalNegociacoes: number;
    valorTotalVendas: number;
    porStatus: Record<string, number>;
    porCanal: Record<string, CanalNegociacaoStats>;
    porVendedor: Record<string, number>;
    porMotivoPerda: Record<string, number>;
};

function emptyCanal(): CanalNegociacaoStats {
    return { total: 0, ganhos: 0, perdidos: 0, emAberto: 0, valorVendas: 0 };
}

export function buildLeadsSummary(
    rawLeads: Lead[],
    userNameMap: Map<string, string>,
    lossReasonMap: Map<string, string>
): LeadsAnalysisSummary {
    const summary: LeadsAnalysisSummary = {
        total: rawLeads.length,
        valorTotalNegociacoes: 0,
        valorTotalVendas: 0,
        porStatus: {},
        porCanal: {},
        porVendedor: {},
        porMotivoPerda: {},
    };

    rawLeads.forEach((lead) => {
        const value = lead.value || 0;
        summary.valorTotalNegociacoes += value;

        const status = lead.status || 'open';
        summary.porStatus[status] = (summary.porStatus[status] || 0) + 1;

        const canal = lead.origin?.trim() || 'Desconhecida';
        if (!summary.porCanal[canal]) {
            summary.porCanal[canal] = emptyCanal();
        }
        const row = summary.porCanal[canal];
        row.total += 1;

        if (status === 'won') {
            row.ganhos += 1;
            row.valorVendas += value;
            summary.valorTotalVendas += value;
        } else if (status === 'lost') {
            row.perdidos += 1;
        } else {
            row.emAberto += 1;
        }

        const vendedor = lead.agent
            ? userNameMap.get(lead.agent) || 'Não atribuído'
            : 'Não atribuído';
        summary.porVendedor[vendedor] = (summary.porVendedor[vendedor] || 0) + 1;

        if (lead.lostReasonId) {
            const motivo = lossReasonMap.get(lead.lostReasonId) || 'Não especificado';
            summary.porMotivoPerda[motivo] = (summary.porMotivoPerda[motivo] || 0) + 1;
        }
    });

    return summary;
}
